import Ajv from 'ajv';
import Sentry from '@saucelabs/sl-util-sentry';
import { isiOS } from '@saucelabs/sl-util-helpers';

import testConfigurationConstants from '../testConfiguration/constants';
import { utils as testConfigurationUtils } from '../testConfiguration';
import * as globalConstants from '../../constants';
import { Preset, PresetPayload } from './records';
import { APP_STORAGE_FILE_ID_PREFIX } from '../../constants';
import SCHEMAS from './payloadSchemas';

const ajv = new Ajv();

const { ANDROID, IOS, UNKNOWN } = testConfigurationConstants.PLATFORMS;
const {
  [ANDROID]: ANDROID_PRETTY,
  [IOS]: IOS_PRETTY,
} = testConfigurationConstants.PLATFORMS_PRETTY;
const {
  WEB_DESKTOP_BROWSER,
  WEB_REAL_DEVICE,
  WEB_VIRTUAL_DEVICE,
  APP_REAL_DEVICE,
  APP_VIRTUAL_DEVICE,
} = globalConstants.CONFIGURATION_COMBINATION_TYPES;

/**
 * This helper function creates a pretty platform string based on VMD configuration values.
 * @param {string}  os - A string representing the OS name ('ios'|'android').
 * @param {string}  version - A string representing the OS version.
 */
function createPrettyVmdPlatform(os: string, version = ''): string {
  const sanitizedOs = os.toLowerCase();

  if (sanitizedOs === ANDROID) {
    return `${ANDROID_PRETTY}${version ? ` ${version}` : ''}`;
  }
  if (isiOS(sanitizedOs) || sanitizedOs === IOS) {
    return `${IOS_PRETTY}${version ? ` ${version}` : ''}`;
  }
  return UNKNOWN;
}

/**
 * This helper function sanitizes the test capabilities (a.k.a. testConfiguration)
 * based on the testType and deviceType so that they can be stored in the Presets service API.
 * This means only the strictly necessary fields are present in the sanitized version,
 * all others are discarded.
 * @param {Record<string, any>}  capabilities - The live test configuration as used in the UI
 * @returns {PresetPayload} The sanitized capabilites (a.k.a. testConfiguration)
 * prepared to be used with the Presets service
 */
const sanitizeCapabilities = (capabilities: Record<string, any>): PresetPayload => { // eslint-disable-line @typescript-eslint/no-explicit-any
  const testCombination = testConfigurationUtils.getConfigurationCombinationType(
    capabilities.testType,
    capabilities.deviceType,
  );
  switch (testCombination) {
    case WEB_DESKTOP_BROWSER:
      return {
        url: capabilities.url,
        tunnel: capabilities.tunnel,
        testType: capabilities.testType,
        deviceType: capabilities.deviceType,
        browserConfiguration: Object.fromEntries(
          Object.entries(capabilities.browserConfiguration).sort(),
        ),
      };
    case WEB_REAL_DEVICE:
      return {
        url: capabilities.url,
        tunnel: capabilities.tunnel,
        testType: capabilities.testType,
        deviceType: capabilities.deviceType,
        // sorting is not needed since it's an immuatable map which is ordered by insertion order,
        realDeviceConfiguration: capabilities.realDeviceConfiguration,
      };
    case WEB_VIRTUAL_DEVICE:
      return {
        url: capabilities.url,
        tunnel: capabilities.tunnel,
        testType: capabilities.testType,
        deviceType: capabilities.deviceType,
        virtualDeviceConfiguration: Object.fromEntries(
          Object.entries(capabilities.virtualDeviceConfiguration).sort(),
        ),
      };
    case APP_REAL_DEVICE:
      return {
        app: `${APP_STORAGE_FILE_ID_PREFIX}${capabilities.mobileAppConfiguration.id as string}`,
        tunnel: capabilities.tunnel,
        testType: capabilities.testType,
        deviceType: capabilities.deviceType,
        // sorting is not needed since it's an immuatable map which is ordered by insertion order
        realDeviceConfiguration: capabilities.realDeviceConfiguration,
      };
    case APP_VIRTUAL_DEVICE:
      return {
        app: `${APP_STORAGE_FILE_ID_PREFIX}${capabilities.mobileAppConfiguration.id as string}`,
        tunnel: capabilities.tunnel,
        testType: capabilities.testType,
        deviceType: capabilities.deviceType,
        // sorting is not needed since it's an immuatable map which is ordered by insertion order
        virtualDeviceConfiguration: Object.fromEntries(
          Object.entries(capabilities.virtualDeviceConfiguration).sort(),
        ),
      };
    default:
      throw new Error('Test type and test device combination is not valid for a preset');
  }
};
/**
 * This function returns the matching relative URL for the UI based on a given Preset.
 * @param {Preset} preset - A Preset as defined in the Preset Service
 * @returns {string} The matching relative URL for the given Preset
 */
const getConfigUrlForPreset = (preset: Preset): string => {
  const testCombination = testConfigurationUtils.getConfigurationCombinationType(
    preset.payload.testType,
    preset.payload.deviceType,
  );

  switch (testCombination) {
    case WEB_DESKTOP_BROWSER:
      return '/live/web-testing/desktop';
    case WEB_REAL_DEVICE:
      return '/live/web-testing/device';
    case WEB_VIRTUAL_DEVICE:
      return '/live/web-testing/virtual';
    case APP_REAL_DEVICE:
      return `/live/app-testing/file/${preset.getAppId()}/device`;
    case APP_VIRTUAL_DEVICE:
      return `/live/app-testing/file/${preset.getAppId()}/virtual`;
    default:
      throw new Error('Test type and test device combination is not valid for a preset');
  }
};

/**
 * Validates presets payload against a JSON Schema
 * @param {Preset} preset - A Preset as defined in the Preset Service
 * @returns {boolean} If it's valid or not
 */
const isValidPresetsSchema = (preset: Preset): boolean => {
  const { deviceType, testType } = preset.payload;
  const schemaType = testConfigurationUtils.getConfigurationCombinationType(testType, deviceType);
  const schema = SCHEMAS[schemaType];

  // don't fail if a schema couldn't be found because other presets might pass
  // but log it to Sentry so we know about that issue
  if (!schema) {
    Sentry.captureException(
      new TypeError(`No JSON schema found for testType "${testType}" and deviceType "${deviceType}"`),
    );
    return false;
  }

  return ajv.compile(schema)(preset.payload);
};

export default {
  createPrettyVmdPlatform,
  getConfigUrlForPreset,
  isValidPresetsSchema,
  sanitizeCapabilities,
};
